import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography, IconButton, Button, TextField } from '@mui/material';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import RemoveIcon from '@mui/icons-material/Remove';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import { createShopCategoryApi, getShopCategoriesApi, updateShopCategoryApi, getShopListApi } from '../../api';
import ConfirmModal from '../common/ConfirmModal';

const ShopCategoryEditModal = ({
  open,
  onSubmit,
  onClose,
}) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialItems, setInitialItems] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const dragIndexRef = useRef(null);
  const [nameErrorIndices, setNameErrorIndices] = useState([]);
  const [deleteWarningMessage, setDeleteWarningMessage] = useState(null);

  useEffect(() => {
    if (!open) return;
    const fetchCategories = async () => {
      try {
        setError(null);
        const res = await getShopCategoriesApi();
        const list = Array.isArray(res?.data) ? res.data : res;
        const normalized = (list || []).map((item) => ({
          categoryId: item.categoryId ?? item.category_id ?? item.id,
          name: item.name,
          sortOrder: item.sortOrder ?? item.sort_order ?? 0,
          isActive:
            typeof item.isActive === 'boolean'
              ? item.isActive
              : item.is_active !== undefined
                ? !!item.is_active
                : true,
          isDeleted: false,
        }));
        setItems(normalized);
        setInitialItems(normalized);
        setNameErrorIndices([]);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load shop categories:', e);
        setError(e.message || '업종 목록을 불러오지 못했습니다.');
      }
    };

    fetchCategories();
  }, [open]);

  if (!open) return null;

  const handleChangeName = (index, value) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, name: value } : item)),
    );
    setNameErrorIndices((prev) => prev.filter((i) => i !== index));
  };

  const handleRemove = (index) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, isDeleted: !item.isDeleted } : item)),
    );
    setDeleteWarningMessage(null);
  };

  const handleAdd = () => {
    setItems((prev) => [
      ...prev,
      {
        id: Date.now(),
        value: '',
        name: '',
        isDeleted: false,
      },
    ]);
  };

  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragOver = (event, index) => {
    event.preventDefault();
    if (dragIndex === null || dragIndex === index) return;

    setItems((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(dragIndex, 1);
      updated.splice(index, 0, moved);
      return updated;
    });

    setDragIndex(index);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  const handleTouchStart = (index) => {
    setDragIndex(index);
    dragIndexRef.current = index;
  };

  const handleTouchMove = (e) => {
    if (e.cancelable) e.preventDefault();

    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!element) return;

    const row = element.closest('[data-index]');
    if (!row) return;

    const newIndex = parseInt(row.getAttribute('data-index'), 10);
    const currentIndex = dragIndexRef.current;

    if (
      newIndex !== null
      && !Number.isNaN(newIndex)
      && currentIndex !== null
      && currentIndex !== newIndex
    ) {
      setItems((prev) => {
        const updated = [...prev];
        const [moved] = updated.splice(currentIndex, 1);
        updated.splice(newIndex, 0, moved);
        return updated;
      });

      setDragIndex(newIndex);
      dragIndexRef.current = newIndex;
    }
  };

  const handleTouchEnd = () => {
    setDragIndex(null);
    dragIndexRef.current = null;
  };

  const handleSubmit = async () => {
    const emptyIdx = items
      .map((item, idx) => (!item.isDeleted && !String(item.name || '').trim() ? idx : null))
      .filter((idx) => idx !== null);
    if (emptyIdx.length > 0) {
      setNameErrorIndices(emptyIdx);
      setError('카테고리 이름을 입력해주세요.');
      return;
    }

    const nameMap = new Map();
    items.forEach((item, idx) => {
      if (item.isDeleted) return;
      const key = (item.name || '').trim();
      if (!key) return;
      const list = nameMap.get(key) || [];
      list.push(idx);
      nameMap.set(key, list);
    });

    const dupIdx = [];
    nameMap.forEach((list) => {
      if (list.length > 1) {
        dupIdx.push(...list);
      }
    });

    if (dupIdx.length > 0) {
      setNameErrorIndices(dupIdx);
      setError('중복된 카테고리 이름이 있습니다. 각 카테고리는 서로 다른 이름이어야 합니다.');
      return;
    }

    // 삭제하려는 카테고리들 중에 등록된 업체가 있는지 확인
    const categoriesToDelete = items.filter((item) => item.isDeleted && item.categoryId);
    const categoriesWithShops = [];

    if (categoriesToDelete.length > 0) {
      try {
        // eslint-disable-next-line no-restricted-syntax
        for (const category of categoriesToDelete) {
          const res = await getShopListApi({ categoryId: category.categoryId, page: 1, limit: 1 });
          const shopCount = res?.data?.total ?? (res?.data?.items?.length ?? 0);
          if (shopCount > 0) {
            categoriesWithShops.push(category.name);
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to check shops for categories:', e);
      }
    }

    if (categoriesWithShops.length > 0) {
      setDeleteWarningMessage(
        `카테고리에 등록된 업체가 있어 삭제할 수 없습니다.`,
      );
      setNameErrorIndices([]);
      setError(null);
      return;
    }

    setDeleteWarningMessage(null);
    setNameErrorIndices([]);
    setConfirmOpen(true);
  };

  const handleConfirmAction = () => {
    setConfirmOpen(false);
    const processSave = async () => {
      try {
        setSaving(true);

        const current = items.map((item, index) => ({
          ...item,
          sortOrder: index + 1,
        }));

        const initialById = new Map(
          initialItems
            .filter((c) => c.categoryId)
            .map((c) => [c.categoryId, c]),
        );

        // eslint-disable-next-line no-restricted-syntax
        for (const item of current) {
          const isNew = !item.categoryId || !initialById.has(item.categoryId);

          if (item.isDeleted) {
            if (!isNew) {
              // eslint-disable-next-line no-await-in-loop
              await updateShopCategoryApi(item.categoryId, {
                isActive: 0,
              });
            }
            continue;
          }

          if (isNew) {
            // eslint-disable-next-line no-await-in-loop
            await createShopCategoryApi({
              name: item.name,
              sortOrder: item.sortOrder,
              isActive: 1,
            });
          } else {
            // eslint-disable-next-line no-await-in-loop
            await updateShopCategoryApi(item.categoryId, {
              name: item.name,
              sortOrder: item.sortOrder,
              isActive: 1,
            });
          }
        }

        const res = await getShopCategoriesApi();
        const list = Array.isArray(res?.data) ? res.data : res;
        const normalized = (list || []).map((item) => ({
          categoryId: item.categoryId ?? item.category_id ?? item.id,
          name: item.name,
          sortOrder: item.sortOrder ?? item.sort_order ?? 0,
          isActive:
            typeof item.isActive === 'boolean'
              ? item.isActive
              : item.is_active !== undefined
                ? !!item.is_active
                : true,
          isDeleted: false,
        }));
        setItems(normalized);
        setInitialItems(normalized);
        setNameErrorIndices([]);

        if (onSubmit) {
          const next = normalized.map((item) => ({
            id: item.categoryId,
            value: item.categoryId,
            label: item.name,
            sortOrder: item.sortOrder,
          }));
          onSubmit(next);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to save shop categories:', e);
        setError(e.message || '업종 저장에 실패했습니다.');
      } finally {
        setSaving(false);
      }
    };

    processSave();
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: 'rgba(0,0,0,0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300,
      }}
      onClick={onClose}
    >
      <Paper
        elevation={3}
        sx={{
          width: 360,
          maxWidth: '90%',
          p: 3,
          pt: 3.5,
          pb: 3.5,
          textAlign: 'center',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
          업종 수정
        </Typography>

        <Box sx={{ mb: 2 }}>
          {!error && items.length === 0 && (
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              등록된 업종이 없습니다.
            </Typography>
          )}

          {items.map((category, index) => {
            const isDragging = dragIndex === index;
            return (
              <Box
                key={category.id ?? category.value ?? index}
                data-index={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: category.isDeleted
                    ? '1px solid #ef9a9a'
                    : isDragging
                      ? '2px solid #0d47a1'
                      : '1px solid #e0e0e0',
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.75,
                  mb: 1,
                  bgcolor: category.isDeleted
                    ? '#ffebee'
                    : isDragging
                      ? '#e3f2fd'
                      : '#ffffff',
                  opacity: category.isDeleted ? 0.8 : isDragging ? 0.9 : 1,
                  transform: isDragging ? 'scale(1.02)' : 'scale(1)',
                  boxShadow: isDragging ? '0 4px 8px rgba(0,0,0,0.1)' : 'none',
                  transition: 'transform 0.2s, box-shadow 0.2s, background-color 0.2s',
                  position: 'relative',
                  zIndex: isDragging ? 1 : 'auto',
                }}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                onDrop={handleDragEnd}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mr: 1.5,
                    touchAction: 'none',
                  }}
                  onTouchStart={() => handleTouchStart(index)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <DragHandleIcon sx={{ fontSize: 20, color: '#333', cursor: 'grab' }} />
                </Box>

                <TextField
                  value={category.name}
                  onChange={(e) => handleChangeName(index, e.target.value)}
                  variant="standard"
                  placeholder="업종 입력"
                  error={nameErrorIndices.includes(index)}
                  disabled={category.isDeleted}
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      fontSize: 15,
                      fontWeight: 600,
                      px: 0,
                      textDecoration: 'none',
                      color: category.isDeleted ? 'text.disabled' : 'inherit',
                    },
                  }}
                  sx={{
                    flex: 1,
                    mr: 0.5,
                    '& .MuiInputBase-root': {
                      borderRadius: 1,
                      border: nameErrorIndices.includes(index)
                        ? '1px solid #f44336'
                        : '1px solid transparent',
                      px: 0.5,
                    },
                  }}
                />

                <IconButton size="small" onClick={() => handleRemove(index)}>
                  {category.isDeleted ? (
                    <AddIcon sx={{ fontSize: 20, color: '#333' }} />
                  ) : (
                    <RemoveIcon sx={{ fontSize: 20, color: '#333' }} />
                  )}
                </IconButton>
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <AddCircleOutlineIcon sx={{ fontSize: 30, color: '#999', cursor: 'pointer' }} onClick={handleAdd} />
        </Box>

        {deleteWarningMessage && (
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Typography
              variant="body2"
              sx={{
                color: '#d32f2f',
                fontWeight: 600,
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              {deleteWarningMessage}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={onClose}
            sx={{
              py: 1.2,
              fontSize: 16,
              fontWeight: 600,
              borderColor: '#d0d0d0',
              color: '#333',
              '&:hover': {
                borderColor: '#b0b0b0',
                bgcolor: '#f8f8f8',
              },
            }}
          >
            취소
          </Button>
          <Button
            variant="contained"
            fullWidth
            sx={{
              bgcolor: '#0d47a1',
              fontWeight: 600,
              py: 1.2,
              fontSize: 16,
              '&:hover': {
                bgcolor: '#0b3a81',
              },
            }}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? '저장 중...' : '수정'}
          </Button>
        </Box>
      </Paper>

      <ConfirmModal
        open={confirmOpen}
        message={deleteWarningMessage ? '업종을 수정하시겠습니까?' : '업종을 수정하시겠습니까?'}
        warningMessage={deleteWarningMessage}
        onConfirm={handleConfirmAction}
        onCancel={() => {
          setConfirmOpen(false);
          setDeleteWarningMessage(null);
        }}
      />
      {error && (
        <Typography
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'rgba(0,0,0,0.75)',
            color: '#fff',
            px: 2,
            py: 1,
            borderRadius: 2,
            fontSize: 12,
            zIndex: 1400,
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default ShopCategoryEditModal;

